declare global {
  interface Window {
    HME?: {
      createH264MP4Encoder(): Promise<Encoder>;
    };
  }
}

interface Encoder {
  initialize(): void;
  finalize(): void;
  delete(): void;
  addFrameRgba(rgba: Uint8ClampedArray): void;

  FS: any;

  temporalDenoise: boolean;
  width: number;
  height: number;
  frameRate: number;
  outputFilename: string;
  debug: boolean;
}

interface EncoderOptions {
  width: number;
  height: number;
  frameRate: number;
}

const ENCODER_URL = "https://unpkg.com/h264-mp4-encoder/embuild/dist/h264-mp4-encoder.web.js";

const loadEncoder = async () => {
  const script = document.createElement("script");

  script.src = ENCODER_URL;

  document.body.appendChild(script);

  return new Promise<void>((resolve) => {
    script.onload = () => {
      resolve();
    };
  });
};

// Safari < 16.4 doesn't support OffscreenCanvas so we need to fallback to a regular canvas
const createCanvas = (width: number, height: number): OffscreenCanvas | HTMLCanvasElement => {
  if (window.OffscreenCanvas) {
    return new OffscreenCanvas(width, height);
  } else {
    const canvas = document.createElement("canvas");

    Object.assign(canvas, {
      width,
      height
    });

    return canvas;
  }
};

export const useMp4Encoder = ({ width, height, frameRate }: EncoderOptions) => {
  const clampedWidth = Math.floor(width / 2) * 2;
  const clampedHeight = Math.floor(height / 2) * 2;
  const canvas = createCanvas(width, height);

  const ctx = canvas.getContext("2d", {
    willReadFrequently: true
  }) as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

  let encoder: Encoder | undefined;

  if (!ctx) {
    throw new Error("Could not get canvas context");
  }

  const initialize = async () => {
    if (!window.HME) {
      await loadEncoder();
    }

    if (!window.HME) {
      throw new Error("Failed to load MP4 encoder");
    }

    encoder = await window.HME.createH264MP4Encoder();
    encoder.width = clampedWidth;
    encoder.height = clampedHeight;
    encoder.frameRate = Math.floor(frameRate);
    encoder.temporalDenoise = true;

    encoder.initialize();
  };

  const addFrame = async (imageData: ImageData) => {
    const bitmap = await createImageBitmap(imageData);
    ctx.drawImage(bitmap, 0, 0, clampedWidth, clampedHeight);
    const data = ctx.getImageData(0, 0, clampedWidth, clampedHeight);
    encoder?.addFrameRgba(data.data);
  };

  const finish = () => {
    if (!encoder) {
      return;
    }

    encoder.finalize();

    const buffer = encoder.FS.readFile(encoder.outputFilename);

    encoder?.delete();

    return buffer;
  };

  return {
    initialize,
    addFrame,
    finish
  };
};
